import type { StructuredBusinessContext } from '@/types/api'

/**
 * The eight `StructuredBusinessContext` keys that drive the canonical
 * Phase 3+ surface (Overview grid + Domain Detail). Order is intentional:
 * the grid renders left-to-right, top-to-bottom in this order, and the
 * Domain Detail header label/sub-field schema lookup uses the same keys.
 *
 * This is the **one place** the frontend tolerates a static domain list.
 * Everything else derives from `StructuredBusinessContext` keys at runtime.
 */
export type DomainKey = keyof StructuredBusinessContext

/** Render hint for a single sub-field row. */
export type DomainFieldKind = 'string' | 'longstring' | 'stringArray' | 'objectArray' | 'object'

export interface DomainFieldDef {
  /** Sub-field name in `StructuredBusinessContext[domain]`. */
  key: string
  /** Display label (Title Case). */
  label: string
  /** Render hint — drives row layout in `TruthFieldRow`. */
  kind: DomainFieldKind
}

export interface DomainDef {
  key: DomainKey
  label: string
  href: string
  fields: DomainFieldDef[]
}

export const DOMAINS: DomainDef[] = [
  {
    key: 'mission',
    label: 'Mission',
    href: '/domains/mission',
    fields: [
      { key: 'vision', label: 'Vision', kind: 'longstring' },
      { key: 'problem', label: 'Problem', kind: 'longstring' },
      { key: 'solution', label: 'Solution', kind: 'longstring' },
      { key: 'valueProposition', label: 'Value Proposition', kind: 'longstring' },
    ],
  },
  {
    key: 'product',
    label: 'Product',
    href: '/domains/product',
    fields: [
      { key: 'features', label: 'Features', kind: 'objectArray' },
      { key: 'techStack', label: 'Tech Stack', kind: 'stringArray' },
      { key: 'mvpScope', label: 'MVP Scope', kind: 'longstring' },
    ],
  },
  {
    key: 'businessModel',
    label: 'Business Model',
    href: '/domains/businessModel',
    fields: [
      { key: 'revenueStreams', label: 'Revenue Streams', kind: 'objectArray' },
      { key: 'pricingStrategy', label: 'Pricing Strategy', kind: 'longstring' },
      { key: 'costStructure', label: 'Cost Structure', kind: 'objectArray' },
    ],
  },
  {
    key: 'targetMarket',
    label: 'Target Market',
    href: '/domains/targetMarket',
    fields: [
      { key: 'primaryPersona', label: 'Primary Persona', kind: 'object' },
      { key: 'marketSize', label: 'Market Size', kind: 'object' },
      { key: 'painPoints', label: 'Pain Points', kind: 'stringArray' },
    ],
  },
  {
    key: 'team',
    label: 'Team',
    href: '/domains/team',
    fields: [
      { key: 'founders', label: 'Founders', kind: 'objectArray' },
      { key: 'keyHires', label: 'Key Hires', kind: 'objectArray' },
    ],
  },
  {
    key: 'goToMarket',
    label: 'Go-to-Market',
    href: '/domains/goToMarket',
    fields: [
      { key: 'channels', label: 'Channels', kind: 'objectArray' },
      { key: 'customerAcquisition', label: 'Customer Acquisition', kind: 'longstring' },
      { key: 'retentionStrategy', label: 'Retention Strategy', kind: 'longstring' },
    ],
  },
  {
    key: 'fundraising',
    label: 'Fundraising',
    href: '/domains/fundraising',
    fields: [
      { key: 'stage', label: 'Stage', kind: 'string' },
      { key: 'targetAmount', label: 'Target Amount', kind: 'string' },
      { key: 'currentRunway', label: 'Current Runway', kind: 'string' },
    ],
  },
  {
    key: 'competition',
    label: 'Competition',
    href: '/domains/competition',
    fields: [
      { key: 'competitors', label: 'Competitors', kind: 'objectArray' },
      { key: 'positioning', label: 'Positioning', kind: 'longstring' },
      { key: 'moat', label: 'Moat', kind: 'longstring' },
    ],
  },
]

export const DOMAIN_BY_KEY: Record<DomainKey, DomainDef> = DOMAINS.reduce(
  (acc, d) => {
    acc[d.key] = d
    return acc
  },
  {} as Record<DomainKey, DomainDef>
)

export function isDomainKey(value: string | undefined | null): value is DomainKey {
  return !!value && Object.prototype.hasOwnProperty.call(DOMAIN_BY_KEY, value)
}

// ─── Completeness helpers ───────────────────────────────────────────────
//
// Frontend-derived completeness — per API-AUDIT, the backend doesn't ship a
// per-domain score yet. Both the Overview grid and the Domain Detail header
// pull from these helpers so the buckets stay consistent.

export type DomainCompleteness = 'complete' | 'partial' | 'thin' | 'empty'

export function isFieldFilled(value: unknown): boolean {
  if (value == null) return false
  if (typeof value === 'string') return value.trim().length > 0
  if (Array.isArray(value)) return value.length > 0
  if (typeof value === 'object') return Object.keys(value as object).length > 0
  return true
}

/**
 * Bucket a domain section into one of four completeness states. Ratio of
 * filled-to-total fields drives the cut. Empty section ⇒ 'empty', not just
 * the ratio's lower bound.
 */
export function deriveCompleteness(
  section: Record<string, unknown> | undefined | null
): DomainCompleteness {
  const entries = Object.entries(section ?? {})
  if (entries.length === 0) return 'empty'
  const filled = entries.filter(([, v]) => isFieldFilled(v)).length
  if (filled === 0) return 'empty'
  const ratio = filled / entries.length
  if (ratio >= 0.85) return 'complete'
  if (ratio >= 0.4) return 'partial'
  return 'thin'
}

/**
 * Bucket the backend's 0-100 domain score into the same four states the UI
 * already speaks. 70 is the analyzer's own cut: below it, it raises a gap.
 *
 * Preferred over deriveCompleteness() when the backend has scored the startup,
 * because it reflects coverage, confidence and freshness rather than how many
 * sub-fields happen to be non-null.
 */
export function completenessFromScore(score: number): DomainCompleteness {
  if (score <= 0) return 'empty'
  if (score >= 70) return 'complete'
  if (score >= 40) return 'partial'
  return 'thin'
}

export function countFilledDomains(
  sourceOfTruth: Partial<StructuredBusinessContext>
): number {
  return DOMAINS.filter(
    (d) =>
      deriveCompleteness(
        (sourceOfTruth[d.key] ?? {}) as Record<string, unknown>
      ) !== 'empty'
  ).length
}
